import type { ActionTrackMetadata } from '@cotera/client/v0/actions/framework';

/**
 * Central registry for every v1 / Coco PostHog analytics event.
 *
 * This is the single source of truth for v1 event names and their payload
 * shapes. It intentionally does NOT change the tracking infrastructure —
 * events still fire through the same two paths:
 *   1. Action `track()` methods returning {@link ActionTrackMetadata}
 *      (use {@link cocoActionMetadata} for compile-time name + payload checks).
 *   2. Direct `analytics.track(name, payload)` / `useTracking(name, args)`
 *      (pass a {@link CocoEvent} constant as the name to avoid typos).
 *
 * Scope: v1 / Coco only. Legacy v0 events (data-grid, slices, command palette,
 * dataset/tool-call field typing, and the v0 `agent_*` events) are deliberately
 * NOT registered here and are left untouched.
 *
 * Naming: existing event-name strings are preserved verbatim so historical
 * continuity in PostHog is not broken. New events use the snake_case `coco_*`
 * convention.
 */

/**
 * What kind of chat a conversation is — the primary usage-split dimension.
 *
 * Derivable today from the loaded conversation / seed:
 *   - `agent`    — a focused agent or an `agent` artifact on the conversation
 *   - `workflow` — a `workflow` artifact on the conversation
 *   - `scratch`  — an ad-hoc chat with none of the above
 *
 * Reserved (kept first-class per product decision, but not derivable from a
 * normal chat mount today — see getChatSurface):
 *   - `shared_link` — surfaced instead via the {@link ChatEntrypoint} of the
 *     share-link activation redirect; a share link *copies* the resource into
 *     the org and lands on the ordinary `/chats/:id` route, so there is no
 *     persistent read-only signal to read.
 *   - `sub_agent` — sub-agent conversations render embedded in their parent and
 *     the client snapshot carries no parent id, so a standalone chat mount
 *     cannot detect it. Passed explicitly by contexts that know.
 */
export type ChatSurface =
  | 'agent'
  | 'workflow'
  | 'scratch'
  | 'shared_link'
  | 'sub_agent';

/** How the user arrived at a chat — complements {@link ChatSurface}. */
export type ChatEntrypoint =
  | 'new'
  | 'from_history'
  | 'from_agent_nav'
  | 'from_workflow_nav'
  | 'command_palette'
  | 'sidebar'
  | 'deep_link'
  | 'shared_link';

/** Props shared by every chat-lifecycle event. */
export interface ChatEventBaseProps {
  conversationId: string;
  surface: ChatSurface;
}

/** Props shared by in-chat action events (surface always present). */
export interface InChatActionProps {
  conversationId: string;
  surface: ChatSurface;
}

// --- Typed payloads ----------------------------------------------------------
// Precise for the core chat-lifecycle + in-chat action events (where `surface`
// must always be present). Other v1 events are registered by name with a
// permissive payload — the priority for those is the shared name constant.

export interface CocoEventPayloads {
  // Chat lifecycle (carry surface; viewed also carries entrypoint)
  coco_chat_viewed: ChatEventBaseProps & {
    orgId: string;
    entrypoint: ChatEntrypoint;
  };
  coco_chat_message_submitted: ChatEventBaseProps & {
    model: string;
    autoApproveTools: boolean;
    messageLength: number;
    hasContent: boolean;
    fileCount: number;
  };
  coco_chat_session_ended: ChatEventBaseProps & Record<string, unknown>;
  coco_chat_stopped: ChatEventBaseProps & { hasMessages: boolean };
  coco_new_chat_created: Record<string, unknown>;
  // Fired when the user attaches files/photos to the composer (before send).
  // conversationId is absent on the new-chat landing (no conversation yet).
  coco_chat_file_attached: {
    surface: ChatSurface;
    fileCount: number;
    source: 'chat' | 'new_chat';
    conversationId?: string;
  };
  coco_chat_opened_from_history: Record<string, unknown>;
  coco_chat_history_viewed: Record<string, unknown>;
  coco_new_chat_viewed: Record<string, unknown>;
  coco_chat_renamed: Record<string, unknown>;

  // Messages (in-chat — surface enforced, other fields preserved as-is)
  coco_message_edited: InChatActionProps & Record<string, unknown>;
  coco_message_replayed: InChatActionProps & Record<string, unknown>;
  coco_message_copied: InChatActionProps & Record<string, unknown>;
  coco_code_block_copied: InChatActionProps & Record<string, unknown>;
  coco_conversation_compacted: InChatActionProps & Record<string, unknown>;

  // Chat modes (in-chat)
  coco_plan_mode_toggled: InChatActionProps & Record<string, unknown>;
  coco_auto_approve_toggled: InChatActionProps & Record<string, unknown>;
  // App-level model preference toggle (localStorage) — not tied to a chat.
  coco_model_switched: Record<string, unknown>;
  coco_voice_transcription_complete: InChatActionProps &
    Record<string, unknown>;

  // Tools (in-chat)
  coco_tool_executed: InChatActionProps & Record<string, unknown>;
  coco_tool_loaded: InChatActionProps & Record<string, unknown>;
  coco_tool_unloaded: InChatActionProps & Record<string, unknown>;
  coco_tool_connections_opened: Record<string, unknown>;
  coco_tool_credentials_submitted: InChatActionProps & Record<string, unknown>;

  // Agents
  coco_agent_created: Record<string, unknown>;
  coco_agent_saved: Record<string, unknown>;
  coco_agent_model_changed: Record<string, unknown>;
  coco_agent_focused: InChatActionProps & { agentId: string };
  coco_agent_unfocused: InChatActionProps;
  coco_agent_viewed: Record<string, unknown>;
  coco_agents_list_viewed: Record<string, unknown>;

  // Documents
  coco_document_created: Record<string, unknown>;
  coco_document_saved: Record<string, unknown>;
  coco_document_viewed: Record<string, unknown>;
  coco_documents_list_viewed: Record<string, unknown>;

  // Datasets
  coco_dataset_viewed: Record<string, unknown>;
  coco_dataset_created: Record<string, unknown>;
  coco_dataset_renamed: Record<string, unknown>;
  coco_dataset_saved: Record<string, unknown>;

  // Workflows
  coco_workflow_created: Record<string, unknown>;
  coco_workflow_saved: Record<string, unknown>;
  coco_workflow_renamed: Record<string, unknown>;
  coco_workflow_viewed: Record<string, unknown>;
  coco_workflows_list_viewed: Record<string, unknown>;
  coco_workflow_run_viewed: Record<string, unknown>;
  coco_workflow_runs_viewed: Record<string, unknown>;

  // Triggers
  coco_trigger_viewed: Record<string, unknown>;
  coco_trigger_renamed: Record<string, unknown>;
  coco_triggers_list_viewed: Record<string, unknown>;
  coco_trigger_manually_run: Record<string, unknown>;
  coco_scheduled_agent_workflow_created: Record<string, unknown>;
  coco_scheduled_tool_workflow_created: Record<string, unknown>;
  coco_webhook_workflow_created: Record<string, unknown>;

  // Sub-agents
  coco_sub_agent_opened: Record<string, unknown>;

  // Folders & resource organization
  coco_folder_created: Record<string, unknown>;
  coco_folder_renamed: Record<string, unknown>;
  coco_folder_archived: Record<string, unknown>;
  coco_folder_viewed: Record<string, unknown>;
  coco_resource_moved_to_folder: Record<string, unknown>;
  coco_resource_archived: Record<string, unknown>;
  coco_resource_favorited: Record<string, unknown>;
  coco_resource_unfavorited: Record<string, unknown>;
  coco_resource_artifact_added: Record<string, unknown>;
  coco_resource_artifact_removed: Record<string, unknown>;
  share_resource_link_created: Record<string, unknown>;
  coco_share_access_changed: Record<string, unknown>;
  coco_share_link_revoked: Record<string, unknown>;
  coco_share_link_opened: Record<string, unknown>;

  // Settings & billing
  coco_settings_opened: Record<string, unknown>;
  coco_add_credits_initiated: Record<string, unknown>;
  coco_out_of_credits_banner_shown: Record<string, unknown>;

  // Navigation / app
  coco_sidebar_nav_clicked: Record<string, unknown>;
  coco_app_version_switched: Record<string, unknown>;
}

/** The set of all registered v1 / Coco event names. */
export type CocoEventName = keyof CocoEventPayloads;

/**
 * Ergonomic constants for referencing event names. Prefer these over string
 * literals at call sites so typos become compile errors.
 */
export const CocoEvent = {
  // Chat lifecycle
  ChatViewed: 'coco_chat_viewed',
  ChatMessageSubmitted: 'coco_chat_message_submitted',
  ChatSessionEnded: 'coco_chat_session_ended',
  ChatStopped: 'coco_chat_stopped',
  NewChatCreated: 'coco_new_chat_created',
  ChatFileAttached: 'coco_chat_file_attached',
  ChatOpenedFromHistory: 'coco_chat_opened_from_history',
  ChatHistoryViewed: 'coco_chat_history_viewed',
  ChatRenamed: 'coco_chat_renamed',
  NewChatViewed: 'coco_new_chat_viewed',

  // Messages
  MessageEdited: 'coco_message_edited',
  MessageReplayed: 'coco_message_replayed',
  MessageCopied: 'coco_message_copied',
  CodeBlockCopied: 'coco_code_block_copied',
  ConversationCompacted: 'coco_conversation_compacted',

  // Chat modes
  PlanModeToggled: 'coco_plan_mode_toggled',
  AutoApproveToggled: 'coco_auto_approve_toggled',
  ModelSwitched: 'coco_model_switched',
  VoiceTranscriptionComplete: 'coco_voice_transcription_complete',

  // Tools
  ToolExecuted: 'coco_tool_executed',
  ToolLoaded: 'coco_tool_loaded',
  ToolUnloaded: 'coco_tool_unloaded',
  ToolConnectionsOpened: 'coco_tool_connections_opened',
  ToolCredentialsSubmitted: 'coco_tool_credentials_submitted',

  // Agents
  AgentCreated: 'coco_agent_created',
  AgentSaved: 'coco_agent_saved',
  AgentModelChanged: 'coco_agent_model_changed',
  AgentFocused: 'coco_agent_focused',
  AgentUnfocused: 'coco_agent_unfocused',
  AgentViewed: 'coco_agent_viewed',
  AgentsListViewed: 'coco_agents_list_viewed',

  // Documents
  DocumentCreated: 'coco_document_created',
  DocumentSaved: 'coco_document_saved',
  DocumentViewed: 'coco_document_viewed',
  DocumentsListViewed: 'coco_documents_list_viewed',

  // Datasets
  DatasetViewed: 'coco_dataset_viewed',
  DatasetCreated: 'coco_dataset_created',
  DatasetRenamed: 'coco_dataset_renamed',
  DatasetSaved: 'coco_dataset_saved',

  // Workflows
  WorkflowCreated: 'coco_workflow_created',
  WorkflowSaved: 'coco_workflow_saved',
  WorkflowRenamed: 'coco_workflow_renamed',
  WorkflowViewed: 'coco_workflow_viewed',
  WorkflowsListViewed: 'coco_workflows_list_viewed',
  WorkflowRunViewed: 'coco_workflow_run_viewed',
  WorkflowRunsViewed: 'coco_workflow_runs_viewed',

  // Triggers
  TriggerViewed: 'coco_trigger_viewed',
  TriggerRenamed: 'coco_trigger_renamed',
  TriggersListViewed: 'coco_triggers_list_viewed',
  TriggerManuallyRun: 'coco_trigger_manually_run',
  ScheduledAgentWorkflowCreated: 'coco_scheduled_agent_workflow_created',
  ScheduledToolWorkflowCreated: 'coco_scheduled_tool_workflow_created',
  WebhookWorkflowCreated: 'coco_webhook_workflow_created',

  // Sub-agents
  SubAgentOpened: 'coco_sub_agent_opened',

  // Folders & resources
  FolderCreated: 'coco_folder_created',
  FolderRenamed: 'coco_folder_renamed',
  FolderArchived: 'coco_folder_archived',
  FolderViewed: 'coco_folder_viewed',
  ResourceMovedToFolder: 'coco_resource_moved_to_folder',
  ResourceArchived: 'coco_resource_archived',
  ResourceFavorited: 'coco_resource_favorited',
  ResourceUnfavorited: 'coco_resource_unfavorited',
  ResourceArtifactAdded: 'coco_resource_artifact_added',
  ResourceArtifactRemoved: 'coco_resource_artifact_removed',
  ShareResourceLinkCreated: 'share_resource_link_created',
  ShareAccessChanged: 'coco_share_access_changed',
  ShareLinkRevoked: 'coco_share_link_revoked',
  ShareLinkOpened: 'coco_share_link_opened',

  // Settings & billing
  SettingsOpened: 'coco_settings_opened',
  AddCreditsInitiated: 'coco_add_credits_initiated',
  OutOfCreditsBannerShown: 'coco_out_of_credits_banner_shown',

  // Navigation / app
  SidebarNavClicked: 'coco_sidebar_nav_clicked',
  AppVersionSwitched: 'coco_app_version_switched',
} as const satisfies Record<string, CocoEventName>;

/**
 * Build a typed {@link ActionTrackMetadata} for an Action `track()` method.
 * Enforces that `event` is a registered name and `properties` matches its
 * payload shape at compile time.
 */
export const cocoActionMetadata = <E extends CocoEventName>(
  event: E,
  properties: CocoEventPayloads[E]
): ActionTrackMetadata => ({
  event,
  properties: properties as Record<string, unknown>,
});
